import { Col, Collapse, Descriptions, Row } from 'antd';

import { AboutCompany } from 'src/components/interviewComponents/aboutCompany';
import { BooleanIcon } from 'src/components/booleanIcon';
import { EditableCollapsePanel } from 'src/components/editableCollapsePanel';
import { GeneralFeedback } from 'src/components/interviewComponents/generalFeedback';
import Item from 'antd/es/descriptions/Item';
import { JobDetails } from 'src/components/interviewComponents/jobDetails';
import React from 'react';
import { ReasonForLeaving } from 'src/components/interviewComponents/reasonForLeaving';
import { StarFilled } from '@ant-design/icons';
import { WagesAndBenefits } from 'src/components/interviewComponents/wagesAndBenefits';
import { useAppSelector } from 'src/types/reduxHelpers';
import { useReasonForLeavingOptions } from 'src/components/interviewComponents/hooks';
import { useTranslation } from 'react-i18next';

interface ReviewSubmitProps {

}

enum ReviewSection {
    personalInfo = 'personalInfo',
    reasonForLeaving = 'reasonForLeaving',
    jobDetails = 'jobDetails',
    wagesAndBenefits = 'wagesAndBenefits',
    aboutCompany = 'aboutCompany',
    generalFeedback = 'generalFeedback',
}

export const ReviewSubmit: React.FC<ReviewSubmitProps> = () => {
    const { t } = useTranslation();

    const staticReasonForLeavingOptions = useReasonForLeavingOptions();

    // State to keep track of active keys (i.e. open panels)
    const [activeKeys, setActiveKeys] = React.useState<Array<string>>([]);
    // State to keep track of keys that are being edited
    const [editKeys, setEditKeys] = React.useState<Record<string, boolean>>({});

    const personalInfo = useAppSelector((state) => state.interview.personalInfo);
    const aboutCompany = useAppSelector((state) => state.interview.aboutCompany);
    const jobDetails = useAppSelector((state) => state.interview.jobDetails);
    const generalFeedback = useAppSelector((state) => state.interview.generalFeedback);
    const reasonsForLeaving = useAppSelector((state) => state.interview.reasonsForLeaving);
    const wagesAndBenefits = useAppSelector((state) => state.interview.wagesAndBenefits);

    const booleanItemContentStyle: React.CSSProperties = {
        width: '80px',
        textAlign: 'center',
    };

    const handleEditStateChanged = (isEditing: boolean, key: ReviewSection): void => {
        setEditKeys({
            ...editKeys,
            [key]: isEditing,
        });

        if (isEditing && !activeKeys.includes(key)) {
            // Open a panel being edited
            setActiveKeys([
                ...activeKeys,
                key,
            ]);
        }
    };

    return (
        <Row
            align="middle"
            justify="center"
            gutter={[30, 20]}
        >
            <Col
                span={24}
            >
                <Collapse
                    activeKey={activeKeys}
                    onChange={(keys) => {
                        const newKeys = [...keys];

                        Object.keys(editKeys).forEach((key) => {
                            if (editKeys[key] === true && !newKeys.includes(key)) {
                                // Only allow a panel to close if its not in edit-mode
                                newKeys.push(key);
                            }
                        });

                        setActiveKeys([...newKeys]);
                    }}
                >
                    <Collapse.Panel
                        header={t('Pages.InterviewPage.steps.personal_info_title')}
                        key={ReviewSection.personalInfo}
                    >
                        <Descriptions
                            bordered={true}
                            column={{
                                xxl: 2,
                                xl: 2,
                                lg: 2,
                                md: 2,
                                sm: 1,
                                xs: 1,
                            }}
                        >
                            <Item
                                label={t('Components.InterviewComponents.PersonalInfo.name_label')}
                            >
                                {personalInfo?.name}
                            </Item>

                            <Item
                                label={t('Components.InterviewComponents.PersonalInfo.department_label')}
                            >
                                {personalInfo?.department}
                            </Item>

                            <Item
                                label={t('Components.InterviewComponents.PersonalInfo.job_title_label')}
                            >
                                {personalInfo?.jobTitle}
                            </Item>

                            <Item
                                label={t('Components.InterviewComponents.PersonalInfo.facility_label')}
                            >
                                {personalInfo?.facilityName}
                            </Item>
                        </Descriptions>
                    </Collapse.Panel>

                    <EditableCollapsePanel
                        header={t('Pages.InterviewPage.steps.reason_for_leaving_title')}
                        key={ReviewSection.reasonForLeaving}
                        editInterviewComponent={ReasonForLeaving}
                        onEditStateChanged={(isEditing) => handleEditStateChanged(isEditing, ReviewSection.reasonForLeaving)}
                    >
                        <Descriptions
                            bordered={true}
                            column={1}
                        >
                            <Item
                                label={t('Components.InterviewComponents.ReasonForLeaving.reason_for_leaving_label')}
                            >
                                <ul>
                                    {reasonsForLeaving?.reasons.map((reason, index) => {
                                        const staticReason = staticReasonForLeavingOptions.find(x => x.value === reason);

                                        if (!!staticReason) {
                                            // This was one of our static 'reason for leaving' options:
                                            // Use the translated label
                                            return (
                                                <li
                                                    key={index}
                                                >
                                                    {staticReason.label}
                                                </li>
                                            );
                                        } else {
                                            // This was the 'other' free-text option that isnt included in our static options:
                                            // Return directly
                                            return (
                                                <li
                                                    key={index}
                                                >
                                                    {reason}
                                                </li>
                                            );
                                        }
                                    })}
                                </ul>
                            </Item>
                        </Descriptions>
                    </EditableCollapsePanel>

                    <EditableCollapsePanel
                        header={t('Pages.InterviewPage.steps.job_details_title')}
                        key={ReviewSection.jobDetails}
                        editInterviewComponent={JobDetails}
                        onEditStateChanged={(isEditing) => handleEditStateChanged(isEditing, ReviewSection.jobDetails)}
                    >
                        <Descriptions
                            bordered={true}
                            column={1}
                        >
                            <Item
                                label={t('Components.InterviewComponents.JobDetails.is_input_valued_label')}
                                contentStyle={booleanItemContentStyle}
                            >
                                <BooleanIcon
                                    value={jobDetails?.isInputValued}
                                />
                            </Item>
                            <Item
                                label={t('Components.InterviewComponents.JobDetails.has_growth_opportunities_label')}
                                contentStyle={booleanItemContentStyle}
                            >
                                <BooleanIcon
                                    value={jobDetails?.hasGrowthOpportunity}
                                />
                            </Item>
                            <Item
                                label={t('Components.InterviewComponents.JobDetails.has_appropriate_responsibilities_label')}
                                contentStyle={booleanItemContentStyle}
                            >
                                <BooleanIcon
                                    value={jobDetails?.hasAppropriateResponsibilities}
                                />
                            </Item>
                        </Descriptions>
                    </EditableCollapsePanel>

                    <EditableCollapsePanel
                        header={t('Pages.InterviewPage.steps.wages_and_benefits_title')}
                        key={ReviewSection.wagesAndBenefits}
                        editInterviewComponent={WagesAndBenefits}
                        onEditStateChanged={(isEditing) => handleEditStateChanged(isEditing, ReviewSection.wagesAndBenefits)}
                    >
                        <Descriptions
                            bordered={true}
                            column={1}
                        >
                            <Item
                                label={t('Components.InterviewComponents.WagesAndBenefits.is_salary_adequate_label')}
                                contentStyle={booleanItemContentStyle}
                            >
                                <BooleanIcon
                                    value={wagesAndBenefits?.isSalaryAdequate}
                                />
                            </Item>
                            <Item
                                label={t('Components.InterviewComponents.WagesAndBenefits.is_bonus_satisfy_label')}
                                contentStyle={booleanItemContentStyle}
                            >
                                <BooleanIcon
                                    value={wagesAndBenefits?.isBonusSatisfy}
                                />
                            </Item>
                            <Item
                                label={t('Components.InterviewComponents.WagesAndBenefits.is_health_benefit_good_label')}
                                contentStyle={booleanItemContentStyle}
                            >
                                <BooleanIcon
                                    value={wagesAndBenefits?.isHealthBenefitGood}
                                />
                            </Item>
                            <Item
                                label={t('Components.InterviewComponents.WagesAndBenefits.is_pension_plan_adequate_label')}
                                contentStyle={booleanItemContentStyle}
                            >
                                <BooleanIcon
                                    value={wagesAndBenefits?.isPensionPlanAdequate}
                                />
                            </Item>
                        </Descriptions>
                    </EditableCollapsePanel>

                    <EditableCollapsePanel
                        header={t('Pages.InterviewPage.steps.company_title')}
                        key={ReviewSection.aboutCompany}
                        editInterviewComponent={AboutCompany}
                        onEditStateChanged={(isEditing) => handleEditStateChanged(isEditing, ReviewSection.aboutCompany)}
                    >
                        <Descriptions
                            bordered={true}
                            column={1}
                        >
                            <Item
                                label={t('Components.InterviewComponents.AboutCompany.is_orientation_useful_label')}
                                contentStyle={booleanItemContentStyle}
                            >
                                <BooleanIcon
                                    value={aboutCompany?.isOrientationUseful}
                                />
                            </Item>

                            <Item
                                label={t('Components.InterviewComponents.AboutCompany.is_safe_work_priority_label')}
                                contentStyle={booleanItemContentStyle}
                            >
                                <BooleanIcon
                                    value={aboutCompany?.isSafeWorkPriority}
                                />
                            </Item>
                            <Item
                                label={t('Components.InterviewComponents.AboutCompany.is_staff_sufficient_label')}
                                contentStyle={booleanItemContentStyle}
                            >
                                <BooleanIcon
                                    value={aboutCompany?.isStaffSufficent}
                                />
                            </Item>
                            <Item
                                label={t('Components.InterviewComponents.AboutCompany.is_facility_clean_label')}
                                contentStyle={booleanItemContentStyle}
                            >
                                <BooleanIcon
                                    value={aboutCompany?.isFacilityClean}
                                />
                            </Item>
                            <Item
                                label={t('Components.InterviewComponents.AboutCompany.is_supervisor_training_provided_label')}
                                contentStyle={booleanItemContentStyle}
                            >
                                <BooleanIcon
                                    value={aboutCompany?.isSupervisorTrainingProvided}
                                />
                            </Item>
                            <Item
                                label={t('Components.InterviewComponents.AboutCompany.is_supervisor_positive_label')}
                                contentStyle={booleanItemContentStyle}
                            >
                                <BooleanIcon
                                    value={aboutCompany?.isSupervisorPositive}
                                />
                            </Item>
                            <Item
                                label={t('Components.InterviewComponents.AboutCompany.is_supervisor_acknowledge_label')}
                                contentStyle={booleanItemContentStyle}
                            >
                                <BooleanIcon
                                    value={aboutCompany?.isSupervisorAcknowledge}
                                />
                            </Item>
                            <Item
                                label={t('Components.InterviewComponents.AboutCompany.is_supervisor_professional_relationship_label')}
                                contentStyle={booleanItemContentStyle}
                            >
                                <BooleanIcon
                                    value={aboutCompany?.isSupervisorProfessionalRelationship}
                                />
                            </Item>
                            <Item
                                label={t('Components.InterviewComponents.AboutCompany.is_open_door_policy_practiced')}
                                contentStyle={booleanItemContentStyle}
                            >
                                <BooleanIcon
                                    value={aboutCompany?.isOpenDoorPolicyPracticed}
                                />
                            </Item>
                            <Item
                                label={t('Components.InterviewComponents.AboutCompany.is_business_inclusive')}
                                contentStyle={booleanItemContentStyle}
                            >
                                <BooleanIcon
                                    value={aboutCompany?.isBusinessInclusive}
                                />
                            </Item>
                        </Descriptions>
                    </EditableCollapsePanel>

                    <EditableCollapsePanel
                        header={t('Pages.InterviewPage.steps.general_feedback_title')}
                        key={ReviewSection.generalFeedback}
                        editInterviewComponent={GeneralFeedback}
                        onEditStateChanged={(isEditing) => handleEditStateChanged(isEditing, ReviewSection.generalFeedback)}
                    >
                        <Descriptions
                            bordered={true}
                            column={1}
                        >
                            <Item
                                label={t('Components.InterviewComponents.GeneralFeedback.is_employee_morale_priority_label')}
                                contentStyle={booleanItemContentStyle}
                            >
                                <BooleanIcon
                                    value={generalFeedback?.isEmployeeMoralePriority}
                                />
                            </Item>

                            <Item
                                label={t('Components.InterviewComponents.GeneralFeedback.is_mental_physic_health_priority_label')}
                                contentStyle={booleanItemContentStyle}
                            >
                                <BooleanIcon
                                    value={generalFeedback?.isMentalPhysicHealthPriority}
                                />
                            </Item>

                            <Item
                                label={t('Components.InterviewComponents.GeneralFeedback.is_expectation_met_label')}
                                contentStyle={booleanItemContentStyle}
                            >
                                <BooleanIcon
                                    value={generalFeedback?.isExpectationMet}
                                />
                            </Item>
                        </Descriptions>

                        <Descriptions
                            layout="vertical"
                            bordered={true}
                            column={1}
                            style={{ marginTop: '10px' }}
                        >
                            <Item
                                label={t('Components.InterviewComponents.GeneralFeedback.new_job_attractivness_label')}
                            >
                                {generalFeedback?.newJobAttractivness}
                            </Item>
                            <Item
                                label={t('Components.InterviewComponents.GeneralFeedback.new_job_deciding_factor_label')}
                            >
                                {generalFeedback?.newJobDecidingFactor}
                            </Item>

                            <Item
                                label={t('Components.InterviewComponents.GeneralFeedback.suggest_to_management_label')}
                            >
                                {generalFeedback?.suggestToManagement}
                            </Item>

                            <Item
                                label={t('Components.InterviewComponents.GeneralFeedback.overall_rating_label')}
                            >
                                {Array(generalFeedback?.overallRating)
                                    .fill(null)
                                    .map((_, i) => (
                                        <StarFilled
                                            key={i}
                                            style={{ color: 'yellow' }}
                                        />
                                    ))}
                            </Item>
                        </Descriptions>
                    </EditableCollapsePanel>
                </Collapse>
            </Col>
        </Row>
    );
};